/**
 * URL building utilities for the application
 * Now uses the centralized webflow path mappings with a simple on/off flag
 */

import {
    getFinalUrl,
    shouldRedirectToWebflow
} from '../constants/webflow-path-mappings';

/**
 * Hook for building URLs with webflow redirect support
 * @returns Object with URL building utilities
 */
export const useUrlBuilder = () => {
    const buildUrl = (path: string): string => {
        // If it's already a full URL, return it as is
        if (
            path.startsWith('http://') ||
            path.startsWith('https://') ||
            path.startsWith('www.')
        ) {
            return path;
        }

        // For relative paths, use getFinalUrl to handle webflow mappings
        return getFinalUrl(path);
    };

    const isWebflowRedirectActive = (path: string): boolean => {
        return shouldRedirectToWebflow(path);
    };

    const getWebflowPath = (path: string): string | null => {
        if (!shouldRedirectToWebflow(path)) return null;
        return getFinalUrl(path).replace('https://www.honeybook.com', '');
    };

    return {
        buildUrl,
        isWebflowRedirectActive,
        getWebflowPath
    };
};

/**
 * Non-hook version for use in JavaScript files and non-React contexts
 * @param path - The Gatsby path to build URL for
 * @returns The final URL (Gatsby or Webflow based on flag)
 */
export const buildUrl = (path: string): string => {
    // If it's already a full URL, return it as is
    if (
        path.startsWith('http://') ||
        path.startsWith('https://') ||
        path.startsWith('www.')
    ) {
        return path;
    }

    // For relative paths, use getFinalUrl to handle webflow mappings
    return getFinalUrl(path);
};

/**
 * Check if a path should redirect to webflow
 * @param path - The path to check
 * @returns true if the path should redirect
 */
export const isWebflowRedirectActive = (path: string): boolean => {
    return shouldRedirectToWebflow(path);
};

/**
 * Get the webflow path for a given gatsby path
 * @param path - The Gatsby path
 * @returns The webflow path or null if no redirect
 */
export const getWebflowPath = (path: string): string | null => {
    if (!shouldRedirectToWebflow(path)) return null;
    return getFinalUrl(path).replace('https://www.honeybook.com', '');
};
